import { useEffect, useMemo, useCallback } from "react"
import { FormControl, FormHelperText } from "@mui/material"
import { observer } from "mobx-react"
import { t } from "@lingui/macro"

import { useStore } from "src/store/lib/useStore"
import { LibraryInformationItemStore } from "src/modals/library-information-items/store"
import { reportError } from "src/lib/report"
import { GlobalStore } from "src/store"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { AutoCompleteTextField } from "src/components/AutoCompleteTextField"

export const LibraryInformationItemsAccessGroup = observer(() => {
    const store = useStore(LibraryInformationItemStore)
    const gstore = useStore(GlobalStore)

    const accessGroupLabels = useMemo(() => {
        return Object.fromEntries(
            store.accessGroups.map((ag) => [ag.id, ag.name]),
        )
    }, [store.accessGroups])

    useEffect(() => {
        ;(async () => {
            try {
                await store.setAccessGroups(gstore.session.accessGroups)
            } catch (error) {
                reportError(
                    t`edit-information-item-modal.load-categories-fail`,
                    error,
                )
            }
        })()
    }, [store, gstore.session.accessGroups])

    useEffect(() => {
        ;(async () => {
            try {
                await store.setSelectedAccessGroupId(
                    gstore.session.accessGroup.id !== undefined
                        ? gstore.session.accessGroup.id
                        : DEFAULT_ACCESS_GROUP.id,
                )
            } catch (error) {
                reportError(
                    t`edit-information-item-modal.load-categories-fail`,
                    error,
                )
            }
        })()
    }, [store, gstore.session.accessGroup])

    const agPickerChange = useCallback(
        async (accessGroupId: number | undefined) => {
            store.formFields.set("accessGroupId", accessGroupId as number)

            const accessGroup = gstore.session.accessGroups.find(
                (ag) => ag.id === accessGroupId,
            )

            if (accessGroup != null) {
                await gstore.session.setSelectedAccessGroup(accessGroup)
            } else {
                // TODO: do this in a better way
                throw new Error("accessgroup could not be selected.")
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [store.formFields.get("accessGroupId"), gstore.session],
    )

    // Get the access group ID outside of useMemo for dependency tracking
    const accessGroupId = store.formFields.get("accessGroupId")

    // Transform the value from a number to an option object
    const selectedOption = useMemo(() => {
        const value =
            store.accessGroups.length > 0 && accessGroupId != null
                ? accessGroupId
                : DEFAULT_ACCESS_GROUP.id

        if (value === undefined) return null
        return {
            id: value,
            label: accessGroupLabels[String(value)] ?? "",
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessGroupLabels, store.accessGroups.length, accessGroupId])

    // Handler for access group selection
    const handleChange = useCallback(
        (_, value) => {
            if (value?.id != null) {
                void agPickerChange(Number(value.id))
            }
        },
        [agPickerChange],
    )

    const getAccessGroupOptions = useCallback(() => {
        return store.accessGroups.map(({ id }) => ({
            id,
            label: accessGroupLabels[String(id)] ?? "",
        }))
    }, [store.accessGroups, accessGroupLabels])

    return (
        <FormControl style={{ breakInside: "avoid" }}>
            <AutoCompleteTextField
                id="accessGroupId"
                options={getAccessGroupOptions()}
                onChange={handleChange}
                value={selectedOption}
                getOptionLabel={(option) => option.label}
                title={t`head.access-group-label`}
                placeholder={t`head.access-group-label`}
                label={t`head.access-group-label`}
                disabled={Boolean(store.isEditMode)}
                autoHighlight
                blurOnSelect
                disableClearable
            />
            {Boolean(store.formFields.error("accessGroupId")) && (
                <FormHelperText error={true}>
                    {store.formFields.error("accessGroupId")}
                </FormHelperText>
            )}
        </FormControl>
    )
})
